"use client";

import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem, SelectGroup } from "@/components/ui/select";
import toast from "react-hot-toast";
import { axiosClient } from "@/lib/axiosClient";
import { useActivePeriod } from "@/hooks/useActivePeriod";
import { Info, Loader2 } from "lucide-react";
import { ITEMS_BLOOD_TYPE, ITEMS_CLOTHES_SIZE, ITEMS_GENDER } from "@/constants/options";
import ClothingSizeModal from "@/components/ClothingSizeModal";

interface PackageItem {
    package_id: number;
    name: string;
    price: string;
    is_active: number | boolean;
    is_header?: number | boolean; // ✅ Tambahan properti is_header untuk filter
    children?: PackageItem[];
}

export interface ParticipantForm {
    participant_id: number;
    full_name: string;
    email: string;
    phone_mobile: string | null;
    gender: string | null;
    blood_type: string | null;
    clothing_size: string | null;
    institution: string | null;
    package_id: number | null;
    registration?: {
        package_id: number;
    } | null;
}

interface ParticipantEditModalProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    participant: ParticipantForm | null;
}

export default function ParticipantEditModal({
    open,
    onClose,
    onSuccess,
    participant,
}: ParticipantEditModalProps) {
    const [form, setForm] = useState<ParticipantForm | null>(participant);
    const [packages, setPackages] = useState<PackageItem[]>([]);
    const [loading, setLoading] = useState(false);
    const { data: activePeriod, isLoading: loadingPeriod } = useActivePeriod();

    useEffect(() => {
        if (participant) {
            // sinkronisasi package_id dari registration (jika ada)
            const packageId =
                participant.package_id ??
                participant.registration?.package_id ??
                null;
            setForm({ ...participant, package_id: packageId });
        }
    }, [participant]);

    useEffect(() => {
        const fetchPackages = async () => {
            if (!activePeriod?.id) return;
            try {
                const { data } = await axiosClient.get<PackageItem[]>(
                    `/packages/period/${activePeriod.id}`
                );
                setPackages(data);
            } catch (err) {
                console.error(err);
                toast.error("Gagal memuat daftar paket.");
            }
        };
        if (open && activePeriod?.id) void fetchPackages();
    }, [open, activePeriod?.id]);

    const handleChange = (
        key: keyof ParticipantForm,
        value: string | number | null
    ) => {
        setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
    };

    interface LaravelValidationError {
        message: string;
        errors?: Record<string, string[]>;
    }

    const handleSubmit = async () => {
        if (!form) return;
        setLoading(false);
        setLoading(true);

        try {
            await axiosClient.put(`/pic/participants/${form.participant_id}`, form);
            toast.success("Peserta berhasil diperbarui.");
            onSuccess();
            onClose();
        } catch (err) {
            // Menggunakan pengecekan instance dari Error atau AxiosError
            const errorResponse = err as {
                response?: {
                    data?: LaravelValidationError;
                    status?: number
                }
            };

            const data = errorResponse.response?.data;

            if (errorResponse.response?.status === 422 && data?.errors) {
                // Menangani error validasi Laravel (Status 422)
                // Mengambil pesan error pertama dari field pertama yang error
                const firstFieldName = Object.keys(data.errors)[0];
                const firstErrorMessage = data.errors[firstFieldName][0];
                toast.error(firstErrorMessage);
            } else if (data?.message) {
                // Menangani error custom dari backend (Status 400, 404, 500 dll)
                toast.error(data.message);
            } else {
                // Fallback jika tidak ada respon dari server
                toast.error("Terjadi kesalahan koneksi atau sistem.");
            }

            console.error("Update Error:", err);
        } finally {
            setLoading(false);
        }
    };

    const renderPackageOptions = (list: PackageItem[]): JSX.Element[] => {
        return list.flatMap((pkg) => {
            const elements: JSX.Element[] = [];
            const isActive = pkg.is_active === 1 || pkg.is_active === true;
            // ✅ FILTER LOGIKA: Cek apakah paket ini Gratis (0) dan merupakan Header
            const isFree = parseFloat(pkg.price) === 0;
            const isHeader = pkg.is_header === 1 || pkg.is_header === true;

            // ✅ Jika BUKAN (Gratis DAN Header), maka tampilkan sebagai opsi
            // "Ladies Program" (Header tapi harga 5jt) akan tetap tampil.
            if (isActive && !(isFree && isHeader)) {
                elements.push(
                    <SelectItem key={pkg.package_id} value={pkg.package_id.toString()}>
                        {pkg.name} — Rp {parseInt(pkg.price, 10).toLocaleString("id-ID")}
                    </SelectItem>
                );
            }

            // ✅ Tetap render children-nya jika ada, meskipun parent-nya disembunyikan
            // Sehingga Rute A-G (Children dari City Tour) tetap akan muncul di dropdown
            if (pkg.children && pkg.children.length > 0) {
                elements.push(...renderPackageOptions(pkg.children));
            }

            return elements;
        });
    };

    const [isClothingSizeOpen, setIsClothingSizeOpen] = useState(false);

    useEffect(() => {
        if (!open) {
            setForm(null);
        }
    }, [open]);

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-lg p-6 max-h-[95vh] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-700">
                <DialogHeader>
                    <DialogTitle className="text-lg font-semibold">
                        Edit Data Peserta
                    </DialogTitle>
                </DialogHeader>

                {form ? (
                    <div className="space-y-5 mt-3">
                        {/* Nama */}
                        <div className="space-y-2">
                            <Label htmlFor="full_name">Nama Lengkap</Label>
                            <Input
                                id="full_name"
                                value={form.full_name}
                                onChange={(e) => handleChange("full_name", e.target.value)}
                                placeholder="Nama peserta"
                                className="input-enhanced"
                            />
                        </div>

                        {/* Email */}
                        <div className="space-y-2">
                            <Label htmlFor="email">Email</Label>
                            <Input
                                id="email"
                                type="email"
                                value={form.email}
                                onChange={(e) => handleChange("email", e.target.value)}
                                placeholder="Email peserta"
                                className="input-enhanced"
                            />
                        </div>

                        {/* Telepon */}
                        <div className="space-y-2">
                            <Label htmlFor="phone_mobile">Nomor HP</Label>
                            <Input
                                id="phone_mobile"
                                value={form.phone_mobile ?? ""}
                                onChange={(e) => handleChange("phone_mobile", e.target.value)}
                                placeholder="08xxxxxxxxxx"
                                className="input-enhanced"
                            />
                        </div>

                        {/* Institusi */}
                        <div className="space-y-2">
                            <Label htmlFor="institution">Institusi</Label>
                            <Input
                                id="institution"
                                value={form.institution ?? ""}
                                onChange={(e) => handleChange("institution", e.target.value)}
                                placeholder="Nama institusi"
                                className="input-enhanced"
                            />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                            {/* Gender */}
                            <div className="space-y-2">
                                <Label htmlFor="gender">Jenis Kelamin</Label>
                                <Select value={form.gender ?? ""} onValueChange={(val) => handleChange("gender", val)}>
                                    <SelectTrigger className={`input-enhanced w-full text-sm dark:bg-gray-700 dark:text-white dark:border-gray-600`}>
                                        <SelectValue placeholder="Jenis Kelamin" />
                                    </SelectTrigger>
                                    <SelectContent className="dark:bg-gray-800 dark:border-gray-700">
                                        <SelectGroup>
                                            {ITEMS_GENDER.map((item) => (
                                                <SelectItem key={item.value} value={item.value}
                                                    className="dark:text-gray-200 dark:focus:bg-gray-700">
                                                    {item.label}
                                                </SelectItem>
                                            ))}
                                        </SelectGroup>
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Blood Type */}
                            <div className="space-y-2">
                                <Label htmlFor="blood_type">Golongan Darah</Label>
                                <Select value={form.blood_type ?? ""} onValueChange={(val) => handleChange("blood_type", val)}>
                                    <SelectTrigger className={`input-enhanced w-full text-sm dark:bg-gray-700 dark:text-white dark:border-gray-600`}>
                                        <SelectValue placeholder="Pilih Golongan" />
                                    </SelectTrigger>
                                    <SelectContent className="dark:bg-gray-800 dark:border-gray-700">
                                        <SelectGroup>
                                            {ITEMS_BLOOD_TYPE.map((item) => (
                                                <SelectItem key={item.value} value={item.value}
                                                    className="dark:text-gray-200 dark:focus:bg-gray-700">
                                                    {item.label}
                                                </SelectItem>
                                            ))}
                                        </SelectGroup>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        {/* Clothing Size */}
                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <Label htmlFor="clothing_size">Ukuran Baju</Label>
                                <button
                                    type="button"
                                    onClick={() => setIsClothingSizeOpen(true)}
                                    className="text-xs md:text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 flex items-center gap-1.5 transition-colors font-medium underline underline-offset-2">
                                    <Info size={14} />
                                    View Size
                                </button>
                            </div>
                            <Select value={form.clothing_size ?? ""} onValueChange={(val) => handleChange("clothing_size", val)}>
                                <SelectTrigger className={`input-enhanced w-full text-sm dark:bg-gray-700 dark:text-white dark:border-gray-600`}>
                                    <SelectValue placeholder="Ukuran Baju" />
                                </SelectTrigger>
                                <SelectContent className="dark:bg-gray-800 dark:border-gray-700">
                                    <SelectGroup>
                                        {ITEMS_CLOTHES_SIZE.map((item) => (
                                            <SelectItem key={item.value} value={item.value}
                                                className="dark:text-gray-200 dark:focus:bg-gray-700">
                                                {item.label}
                                            </SelectItem>
                                        ))}
                                    </SelectGroup>
                                </SelectContent>
                            </Select>
                            <ClothingSizeModal
                                isOpen={isClothingSizeOpen}
                                onOpenChange={setIsClothingSizeOpen}
                            />
                        </div>

                        {/* Paket */}
                        <div className="space-y-2">
                            <Label htmlFor="package">Paket</Label>
                            {loadingPeriod ? (
                                <p className="text-sm text-gray-500">Memuat paket...</p>
                            ) : (
                                <Select
                                    value={
                                        form.package_id ? form.package_id.toString() : undefined
                                    }
                                    onValueChange={(val) =>
                                        handleChange("package_id", parseInt(val, 10))
                                    }
                                >
                                    <SelectTrigger
                                        id="package"
                                        className="input-enhanced border border-gray-300 dark:border-gray-700 rounded-md h-10 focus-visible:ring-1 focus-visible:ring-blue-500 focus-visible:border-blue-500"
                                    >
                                        <SelectValue placeholder="Pilih paket peserta" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {packages.length > 0 ? (
                                            renderPackageOptions(packages)
                                        ) : (
                                            <SelectItem value="0" disabled>
                                                Tidak ada paket tersedia
                                            </SelectItem>
                                        )}
                                    </SelectContent>
                                </Select>
                            )}
                        </div>
                    </div>
                ) : (
                    <p className="text-center text-gray-500 py-6">
                        Data peserta tidak ditemukan.
                    </p>
                )}

                <DialogFooter className="mt-6 flex justify-end gap-3">
                    <Button variant="outline" onClick={onClose} disabled={loading}>
                        Batal
                    </Button>
                    <Button onClick={handleSubmit} disabled={loading}>
                        {loading ? (
                            <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Menyimpan...
                            </>
                        ) : (
                            "Simpan Perubahan"
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
